import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAdminArticles, getSubscribers, getDailyViews, getDailyViewsDetail, getAdInquiries, deleteAdInquiry } from '@/lib/api';
import { FileText, Users, Eye, TrendingUp, Plus, Calendar, ChevronLeft, ChevronRight, Megaphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';

const AdminDashboard = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalArticles: 0,
    publishedArticles: 0,
    totalViews: 0,
    subscribers: 0,
  });
  const [recentArticles, setRecentArticles] = useState([]);
  const [dailyViews, setDailyViews] = useState([]);
  const [dayIndex, setDayIndex] = useState(0);
  const [dayDetail, setDayDetail] = useState(null);
  const [adInquiries, setAdInquiries] = useState([]);

  // Get date string for offset from today (UTC to avoid timezone issues)
  const getDateStr = (offset) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - offset);
    return d.toUTCString().split(' ').slice(0,4).join(' ');
  };

  const getDateISO = (offset) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - offset);
    return d.toISOString().split('T')[0];
  };

  const formatDateLabel = (dateStr) => {
    const today = getDateISO(0);
    const yesterday = getDateISO(1);
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    const d = new Date(dateStr + 'T12:00:00Z');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    const fetchData = async () => {
      try {
        const articlesRes = await getAdminArticles().catch(() => ({ data: [] }));
        const subscribersRes = await getSubscribers().catch(() => ({ data: [] }));
        const dailyRes = await getDailyViews().catch(() => ({ data: [] }));
        const inquiriesRes = await getAdInquiries().catch(() => ({ data: [] }));

        const articles = articlesRes.data || [];
        const published = articles.filter((a) => a.status === 'published');
        const totalViews = articles.reduce((sum, a) => sum + (a.views || 0), 0);

        setStats({
          totalArticles: articles.length,
          publishedArticles: published.length,
          totalViews,
          subscribers: (subscribersRes.data || []).length,
        });

        setRecentArticles(articles.slice(0, 5));
        setDailyViews(dailyRes.data || []);
        setAdInquiries(inquiriesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };

    fetchData();
  }, [isAuthenticated, navigate]);

  // Fetch daily detail when day changes
  useEffect(() => {
    const fetchDayDetail = async () => {
      try {
        const dateStr = getDateISO(dayIndex);
        const res = await getDailyViewsDetail(dateStr);
        setDayDetail(res.data);
      } catch {
        setDayDetail({ date: getDateISO(dayIndex), total_views: 0, stories: [] });
      }
    };
    if (isAuthenticated) fetchDayDetail();
  }, [dayIndex, isAuthenticated]);

  if (!isAuthenticated) return null;

  const handleDeleteInquiry = async (id) => {
    try {
      await deleteAdInquiry(id);
      setAdInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success('Inquiry removed');
    } catch {
      toast.error('Failed to remove inquiry');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white">Dashboard</h2>
            <p className="text-gray-500 mt-1">Manage your content</p>
          </div>
          <Link
            to="/admin/articles/new"
            className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white font-medium hover:bg-yellow-600 transition-colors"
            data-testid="new-article-btn"
          >
            <Plus size={18} />
            Add New Story
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#111] border border-[#222] p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#222] rounded">
                <FileText size={20} className="text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalArticles}</p>
                <p className="text-xs text-gray-500">Stories</p>
              </div>
            </div>
          </div>

          <div className="bg-[#111] border border-[#222] p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#222] rounded">
                <TrendingUp size={20} className="text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.publishedArticles}</p>
                <p className="text-xs text-gray-500">Published</p>
              </div>
            </div>
          </div>

          <div className="bg-[#111] border border-[#222] p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#222] rounded">
                <Eye size={20} className="text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalViews.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Views</p>
              </div>
            </div>
          </div>

          <div className="bg-[#111] border border-[#222] p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#222] rounded">
                <Users size={20} className="text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.subscribers}</p>
                <p className="text-xs text-gray-500">Subscribers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Views Breakdown - optional drill-down */}
        <div className="bg-[#111] border border-[#222] mb-8">
          <div className="p-4 border-b border-[#222] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2">
              <Calendar size={16} className="text-yellow-500" />
              View by Day
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDayIndex(dayIndex + 1)}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-[#222] rounded transition-colors"
                data-testid="day-back-btn"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-300 min-w-[100px] text-center font-medium">
                {dayDetail ? formatDateLabel(dayDetail.date) : '...'}
              </span>
              <button
                onClick={() => setDayIndex(Math.max(0, dayIndex - 1))}
                disabled={dayIndex === 0}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-[#222] rounded transition-colors disabled:opacity-30"
                data-testid="day-forward-btn"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {dayDetail && (
            <div className="p-4">
              <div className="text-right mb-2">
                <span className="text-green-500 font-bold">{(dayDetail.total_views || 0).toLocaleString()} views</span>
              </div>

              {dayDetail.stories && dayDetail.stories.length > 0 ? (
                <div className="space-y-1">
                  {dayDetail.stories.map((story) => (
                    <div key={story.id} className="flex items-center justify-between py-1.5 border-b border-[#1a1a1a]">
                      <span className="text-white text-sm truncate flex-1 mr-3">{story.title}</span>
                      <span className="text-green-500 text-sm font-medium whitespace-nowrap">{story.views}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm text-center py-3">No views recorded this day</p>
              )}
            </div>
          )}
        </div>

        {/* Ad Inquiries - from "Advertise With Us" popup form */}
        <div className="bg-[#111] border border-[#222] mb-8" data-testid="ad-inquiries-section">
          <div className="p-4 border-b border-[#222] flex items-center gap-2">
            <Megaphone size={16} className="text-yellow-500" />
            <h3 className="text-sm font-semibold text-gray-400">Advertiser Inquiries ({adInquiries.length})</h3>
          </div>

          {adInquiries.length > 0 ? (
            <div className="divide-y divide-[#222] max-h-96 overflow-y-auto">
              {adInquiries.map((inquiry) => (
                <div key={inquiry.id} className="p-4 flex items-start justify-between gap-4" data-testid={`ad-inquiry-${inquiry.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-white">{inquiry.name}</h4>
                      {inquiry.company && (
                        <span className="text-xs text-gray-500">· {inquiry.company}</span>
                      )}
                    </div>
                    <a href={`mailto:${inquiry.email}`} className="text-sm text-yellow-500 hover:text-yellow-400">
                      {inquiry.email}
                    </a>
                    <p className="text-sm text-gray-400 mt-1 break-words">{inquiry.message}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {inquiry.created_at ? new Date(inquiry.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteInquiry(inquiry.id)}
                    className="text-gray-500 hover:text-yellow-500 transition-colors flex-shrink-0"
                    data-testid={`ad-inquiry-delete-${inquiry.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500 text-sm">
              No advertiser inquiries yet
            </div>
          )}
        </div>

        {/* Quick Add */}
        <div className="bg-[#111] border border-[#222] p-8 mb-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Ready to post?</h3>
          <p className="text-gray-500 mb-4">Create a new story</p>
          <Link
            to="/admin/articles/new"
            className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors text-lg"
          >
            <Plus size={24} />
            ADD NEW STORY
          </Link>
        </div>

        {/* Recent */}
        <div className="bg-[#111] border border-[#222]">
          <div className="p-6 border-b border-[#222] flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Recent Stories</h3>
            <Link to="/admin/articles" className="text-sm text-yellow-500 hover:text-yellow-400">
              View All
            </Link>
          </div>
          
          {recentArticles.length > 0 ? (
            <div className="divide-y divide-[#222]">
              {recentArticles.map((article) => (
                <div key={article.id} className="p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">{article.title}</h4>
                    <p className="text-sm text-gray-500">
                      {article.category} • <span className="text-green-500">{article.views || 0} views</span>
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium ${
                    article.status === 'published' 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    {article.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No stories yet
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
